import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty, size } from 'lodash'
import { defaultTableConfigsWithMultiple } from 'pages/Administration/components/Table/configs'
import { usersColumn } from './config'
import Table from 'pages/Administration/components/Table'
import {
  getSimpleNodeId,
  handleColumnDefs,
  onExportData,
  sizeTableToFit,
  excelStyles,
  sendNotification
} from 'pages/Administration/helpers'

import {
  FIELD_NAME,
  defaultActions,
  frameworkComponents
} from 'pages/Administration/constants'
import { deleteUser, fetchUser } from 'api/administration/rolesUsersService'
import { useAgGridTranslation } from 'utils/hooks/useDocumentTranslation'
import { openModal } from 'components/Modal/actions'

const UsersPanel = ({ users, roles }) => {
  const { t, i18n } = useTranslation(['Administration', 'Common'])

  const [gridApi, setGridApi] = useState(null)
  const [columnApi, setColumnApi] = useState(null)
  const [, setSelectedRows] = useState([])

  const handleOpenModal = (e) => {
    const { data, colDef } = e

    if (colDef.colId === FIELD_NAME) {
      openModal('administrationUser', {
        modalType: 'UpdateUser',
        roles: roles,
        users: users,
        userValue: { ...data, roles: formatValue(data) },
        gridApi: gridApi
      })
    }
  }

  const handleAddUser = () => {
    openModal('administrationUser', {
      modalType: 'AddUser',
      roles: roles,
      users: users,
      gridApi: gridApi
    })
  }

  const formatValue = (data) =>
    data.roles.map((role) => ({
      label: role,
      value: role
    }))

  const onRowSelect = useCallback(
    (rowList) => {
      setSelectedRows(rowList)
    },
    [setSelectedRows]
  )

  const onDeleteUserSubmit = async () => {
    for (const element of gridApi?.getSelectedNodes()) {
      try {
        await deleteUser(element.data.id)

        gridApi?.deselectAll()

        sendNotification({
          t,
          info: 'Delete',
          kind: 'success',
          message: t('Administration:RolesManagement.DeletedUser')
        })
      } catch (err) {
        sendNotification({
          t,
          info: 'Delete',
          kind: 'error',
          message: t('Administration:RolesManagement.DeletedUserError')
        })
        console.error(err)
      }
    }
    await fetchUser()
  }

  const onDeleteUser = () => {
    openModal('confirmAction', {
      modalHeading: t('Administration:RolesManagement.DeleteUser'),
      message: t('Common:UserDeletionConfirmation'),
      onConfirm: onDeleteUserSubmit,
      loadingOverlay: true
    })
  }

  const headerActions = useMemo(() => {
    if (!isEmpty(gridApi)) {
      return {
        selected: [
          {
            id: 'deleteRole',
            title: t('Administration:RolesManagement.Actions.Delete'),
            handleClick: () => {
              onDeleteUser()
            }
          }
        ],
        empty: [
          {
            id: 'exportRole',
            title: t('Administration:RolesManagement.Actions.ExportAll'),
            handleClick: () => {
              gridApi &&
                columnApi &&
                onExportData(
                  gridApi,
                  columnApi,
                  t('Administration:UsersManagement.Title')
                )
            }
          },
          {
            id: 'addRole',
            title: t('Administration:RolesManagement.Actions.InsertNewRole'),
            handleClick: handleAddUser
          }
        ]
      }
    }
    return defaultActions
  }, [t, gridApi, onDeleteUser, onExportData, handleAddUser])

  const { t: agGridT, destroyed } = useAgGridTranslation()

  return (
    <div className={'mt-3'}>
      {/*<h2 className='no-action-view'>*/}
      {/*  {t('Administration:UsersManagement.Title')}*/}
      {/*</h2>*/}
      <Table
        headerActions={headerActions}
        columnDefs={handleColumnDefs(usersColumn, t)}
        rowData={size(users) ? users : []}
        getRowNodeId={getSimpleNodeId}
        onCellClicked={handleOpenModal}
        excelStyles={excelStyles}
        onGridReady={({ api, columnApi }) => {
          setGridApi(api)
          setColumnApi(columnApi)
          sizeTableToFit(api)
        }}
        key={i18n.language}
        localeTextFunc={agGridT}
        onSelectionChanged={onRowSelect}
        frameworkComponents={frameworkComponents}
        defaultTableConfigs={defaultTableConfigsWithMultiple}
      />
    </div>
  )
}

export default React.memo(UsersPanel)
